// Shared rendering helpers for the site's star-chart SVGs: the ra/dec -> pixel
// projector, star colour/size-by-magnitude, and greedy label de-collision.
// Used by SkyMap.js (per-image locator charts) and the FOV tool's target
// framing chart, so both look and behave the same way.

export const buildProjector = (frame, width, height, pad) => {
    const { raMin, raMax, decMin, decMax } = frame;
    const decCenter = (decMin + decMax) / 2;
    const cosDec = Math.cos((decCenter * Math.PI) / 180);

    const raSpan = (raMax - raMin) * cosDec;
    const decSpan = decMax - decMin;
    const scale = Math.min((width - 2 * pad) / raSpan, (height - 2 * pad) / decSpan);

    const offsetX = (width - raSpan * scale) / 2;
    const offsetY = (height - decSpan * scale) / 2;

    const project = (ra, dec) => ({
        // RA increases to the left, matching the view of the real sky.
        x: offsetX + (raMax - ra) * cosDec * scale,
        y: offsetY + (decMax - dec) * scale
    });
    project.scale = scale;
    return project;
};

// Approximate star colour from B-V colour index (blue -> white -> gold -> red).
export const starColor = (ci) => {
    if (ci == null) return '#f4f6ff';
    if (ci < 0.0) return '#cbd8ff';
    if (ci < 0.3) return '#eaf0ff';
    if (ci < 0.6) return '#fbfbf0';
    if (ci < 0.9) return '#fff3d4';
    if (ci < 1.3) return '#ffe1b0';
    return '#ffc79a';
};

export const starRadius = (mag) => Math.max(0.6, 4.3 - mag * 0.56);
export const starOpacity = (mag) => Math.max(0.35, Math.min(1, 1.15 - (mag - 2) * 0.12));

// Greedy label placement: higher-priority labels are placed first and lower
// ones nudge vertically (or hide) to avoid overlap. Deterministic, so a
// prerendered SVG and the browser render match.
export const layoutLabels = (candidates) => {
    const placed = [];
    const overlaps = (a, b) => !(a.x2 < b.x1 || a.x1 > b.x2 || a.y2 < b.y1 || a.y1 > b.y2);
    const result = [];

    candidates
        .map((c, i) => ({ ...c, i }))
        .sort((a, b) => b.priority - a.priority || a.i - b.i)
        .forEach((c) => {
            const w = c.text.length * c.size * (c.spaced ? 0.82 : 0.58);
            const h = c.size;
            const offsets = [0, -12, 12, -22, 22, -32, 32];
            for (let k = 0; k < offsets.length; k += 1) {
                const y = c.y + offsets[k];
                const x1 = c.anchor === 'middle' ? c.x - w / 2 : c.x;
                const box = { x1, x2: x1 + w, y1: y - h, y2: y + 3 };
                if (!placed.some((p) => overlaps(p, box))) {
                    placed.push(box);
                    result.push({ ...c, y });
                    return;
                }
            }
            if (c.priority >= 3) { // target / nearby objects always shown
                result.push({ ...c, y: c.y });
            }
        });

    return result;
};
